import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import apiClient from "../constants/apiClient";
import { toast } from "../utils/toast";

export type RipeningProgramMode = "ripening" | "storage" | "ripewise";

export interface RipewisePhase {
    phase: number;
    // * The controller's name for the phase (the slot name); older programs have none.
    name?: string;
    values: Record<string, number>;
}

export interface RipewiseSettings {
    phases?: RipewisePhase[];
    general?: Record<string, number>;
    // * The room's store settings (Store_*): alone, a storage program.
    store?: Record<string, number>;
}

export interface StorageSettings {
    setpoint: number;
    air_offset?: number;
}

export interface RipeningProgramDefinition {
    mode: RipeningProgramMode;
    storage?: StorageSettings | null;
    ripewise?: RipewiseSettings | null;
}

export interface RipeningProgram {
    id: string;
    gateway_id: string;
    name: string;
    fruit_type: string;
    duration_minutes: number;
    active: boolean;
    version: number;
    definition: RipeningProgramDefinition;
    updated_at: string;
}

export type RipeningProgramInput = Omit<RipeningProgram, "id" | "gateway_id" | "version" | "updated_at">;

export interface RipeningProgramListResponse {
    results: RipeningProgram[];
    count: number;
}

export interface HealthHop {
    ok: boolean;
    detail?: string;
    latency_ms?: number;
    last_seen?: string;
}

export interface GatewayRipeningHealth {
    ok: boolean;
    checked_at: string;
    hops: {
        service: HealthHop;
        broker: HealthHop;
        gateway: HealthHop;
        reliance: HealthHop;
    };
}

export type RipeningCommandStatus = "pending" | "accepted" | "rejected" | "timeout";

export interface RipeningCommand {
    command_id: string;
    status: RipeningCommandStatus;
    ok?: boolean;
    detail?: string;
    issued_at?: string;
    acknowledged_at?: string;
}

export interface StartRipeningProgramInput {
    program_id: string;
    start_at: string;
    dry_run?: boolean;
}

/**
 * Tenant every ripening-program-service request carries.
 *
 * The backend verifies the tenant against its own records (the gateway's tenant and
 * the current user's tenant) and only forwards the verified value: the program service
 * uses the tenant to pick which tenant's gateways it talks to, so it is never trusted
 * as sent. No facility is sent (intentionally).
 */
export interface RipeningServiceScope {
    tenantId?: string | null;
}

export interface GatewayRipeningScope {
    tenant_id: string;
}

const gatewayEndpoint = (gatewayId: string | undefined) => `/device/ripening-programs/${gatewayId}`;

export function scopeParams(scope: RipeningServiceScope): { tenant_id?: string } {
    return scope.tenantId ? { tenant_id: scope.tenantId } : {};
}

const hasTenantScope = (scope: RipeningServiceScope) => !!scope.tenantId;

/** Tenant of a gateway, as known to the backend (used where no room is in context). */
export function useGatewayRipeningScope(gatewayId: string | undefined) {
    return useQuery<GatewayRipeningScope>({
        queryKey: ["gatewayRipeningScope", gatewayId],
        queryFn: async () => {
            const response = await apiClient.get(`${gatewayEndpoint(gatewayId)}/scope`, { no_toast: true });
            return response.data;
        },
        enabled: !!gatewayId,
        retry: false,
    });
}

export function useRipeningPrograms(
    gatewayId: string | undefined,
    scope: RipeningServiceScope,
    fruitType?: string,
    enabled = true,
) {
    const queryClient = useQueryClient();
    const baseEndpoint = `${gatewayEndpoint(gatewayId)}/programs`;
    const params = scopeParams(scope);
    const invalidatePrograms = () => queryClient.invalidateQueries({
        queryKey: ["ripeningPrograms", gatewayId],
        exact: false,
    });

    const query = useQuery<RipeningProgramListResponse>({
        queryKey: ["ripeningPrograms", gatewayId, params.tenant_id, fruitType],
        queryFn: async () => {
            if (!gatewayId) return { results: [], count: 0 };
            const response = await apiClient.get(baseEndpoint, {
                params: { ...params, ...(fruitType ? { fruit_type: fruitType } : {}) },
                no_toast: true,
            });
            return response.data;
        },
        enabled: !!gatewayId && !!scope.tenantId && enabled,
        retry: false,
    });

    const createMutation = useMutation({
        mutationFn: async (program: RipeningProgramInput) => {
            if (!gatewayId) throw new Error("Gateway ID is required");
            const response = await apiClient.post(baseEndpoint, { ...program, ...params });
            return response.data as RipeningProgram;
        },
        onSuccess: () => {
            invalidatePrograms();
            toast.success("Ripening program created");
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ programId, program }: { programId: string; program: RipeningProgramInput }) => {
            if (!gatewayId) throw new Error("Gateway ID is required");
            const response = await apiClient.put(`${baseEndpoint}/${encodeURIComponent(programId)}`, { ...program, ...params });
            return response.data as RipeningProgram;
        },
        onSuccess: () => {
            invalidatePrograms();
            toast.success("Ripening program updated");
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (programId: string) => {
            if (!gatewayId) throw new Error("Gateway ID is required");
            await apiClient.delete(`${baseEndpoint}/${encodeURIComponent(programId)}`, { params });
        },
        onSuccess: () => {
            invalidatePrograms();
            toast.success("Ripening program deleted");
        },
    });

    return { ...query, createMutation, updateMutation, deleteMutation };
}

export function useGatewayRipeningHealth(gatewayId: string | undefined, scope: RipeningServiceScope) {
    const params = scopeParams(scope);
    return useQuery<GatewayRipeningHealth>({
        queryKey: ["gatewayRipeningHealth", gatewayId, params.tenant_id],
        queryFn: async () => {
            const response = await apiClient.get(`${gatewayEndpoint(gatewayId)}/health`, {
                params,
                no_toast: true,
            });
            return response.data;
        },
        enabled: !!gatewayId && hasTenantScope(scope),
        retry: false,
        refetchInterval: 15000,
    });
}

export function useStartRipeningProgram(
    gatewayId: string | undefined,
    roomId: string | undefined,
    scope: RipeningServiceScope,
) {
    return useMutation<RipeningCommand, Error, StartRipeningProgramInput>({
        mutationFn: async (input) => {
            if (!gatewayId || !roomId) throw new Error("Gateway ID and room ID are required");
            if (!hasTenantScope(scope)) throw new Error("Tenant is required");
            const response = await apiClient.post(
                `${gatewayEndpoint(gatewayId)}/rooms/${encodeURIComponent(roomId)}/start`,
                { ...input, ...scopeParams(scope) },
            );
            return response.data;
        },
    });
}

export interface RipeningCommandPollOptions {
    // * Poll interval while the command is pending (default 2 s).
    intervalMs?: number;
    // * Never toast failed polls, and keep polling through them (background status checks).
    silent?: boolean;
}

export function useRipeningCommand(
    gatewayId: string | undefined,
    commandId: string | undefined,
    scope: RipeningServiceScope,
    { intervalMs = 2000, silent = false }: RipeningCommandPollOptions = {},
) {
    const params = scopeParams(scope);
    return useQuery<RipeningCommand>({
        queryKey: ["ripeningCommand", gatewayId, commandId, params.tenant_id],
        queryFn: async () => {
            const response = await apiClient.get(
                `${gatewayEndpoint(gatewayId)}/commands/${encodeURIComponent(commandId as string)}`,
                silent ? { params, no_toast: true } : { params },
            );
            return response.data;
        },
        enabled: !!gatewayId && !!commandId && !!scope.tenantId,
        ...(silent ? { retry: false } : {}),
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (status === "pending") return intervalMs;
            // * A silent poll also keeps going before the first answer and after a failed poll.
            return silent && !status ? intervalMs : false;
        },
    });
}

export interface RipeningRoomRun {
    state?: string | null;
    recipe?: string | null;
    product?: string | null;
    phase?: string | number | null;
    // * Number of the active phase; not sent by every service version.
    phase_number?: number | null;
    // * The service sends a display string such as "57%"; accept a plain number too.
    progress?: string | number | null;
    // * Progress of the whole program as a number (0..100); not sent by every service version.
    progress_pct?: number | null;
    // * Progress of the current phase as a number (0..100, % of its duration); not sent by every service version.
    phase_progress_pct?: number | null;
    start_at?: string | null;
    end_at?: string | null;
    // * Every phase has run; the room holds the last phase's temperature until it is stopped.
    program_finished?: boolean | null;
    // * The blue low-oxygen lamp at the door is on. Absent: unknown (older service, or the room is not online).
    low_o2_lamp?: boolean | null;
    // * N2 injection is on in the current phase. Absent: unknown.
    n2_active?: boolean | null;
}

export interface RipeningRoomStatus {
    room_id: string;
    gateway_id: string;
    status: "online" | "stale" | "unknown";
    last_raw_at?: string | null;
    run?: RipeningRoomRun | null;
}

export const ROOM_RUN_POLL_MS = 60000;
export const ROOM_RUN_START_PENDING_POLL_MS = 5000;
// * Wait after 1, 2 and 3+ failed polls in a row.
export const ROOM_RUN_ERROR_BACKOFF_MS = [60000, 120000, 300000];

/** Poll interval of the room status: fast while a start is pending, backing off after errors. */
export function roomRunPollInterval(consecutiveErrors: number, startPending: boolean): number {
    if (consecutiveErrors > 0) {
        return ROOM_RUN_ERROR_BACKOFF_MS[Math.min(consecutiveErrors, ROOM_RUN_ERROR_BACKOFF_MS.length) - 1];
    }
    return startPending ? ROOM_RUN_START_PENDING_POLL_MS : ROOM_RUN_POLL_MS;
}

export interface RipeningRoomRunOptions {
    // * A program start is waiting for the room: poll every 5 s instead of every minute.
    startPending?: boolean;
}

/** Every room of a gateway with its live status, e.g. to pick the room a cell is. Silent. */
export function useRipeningRooms(gatewayId: string | undefined | null, scope: RipeningServiceScope) {
    const params = scopeParams(scope);
    return useQuery<RipeningRoomStatus[]>({
        queryKey: ["ripeningRooms", gatewayId, params.tenant_id],
        queryFn: async () => {
            const response = await apiClient.get(`${gatewayEndpoint(gatewayId as string)}/rooms`, { params, no_toast: true });
            return response.data;
        },
        enabled: !!gatewayId && hasTenantScope(scope),
        retry: false,
        refetchOnWindowFocus: false,
    });
}

/** Live run status (program/phase) of a room. Silent: never toasts, never retries. */
export function useRipeningRoomRun(
    gatewayId: string | undefined | null,
    roomId: string | undefined | null,
    scope: RipeningServiceScope,
    { startPending = false }: RipeningRoomRunOptions = {},
) {
    const params = scopeParams(scope);
    const consecutiveErrors = useRef(0);
    return useQuery<RipeningRoomStatus>({
        queryKey: ["ripeningRoomRun", gatewayId, roomId, params.tenant_id],
        queryFn: async () => {
            try {
                const response = await apiClient.get(
                    `${gatewayEndpoint(gatewayId as string)}/rooms/${encodeURIComponent(roomId as string)}`,
                    { params, no_toast: true },
                );
                consecutiveErrors.current = 0;
                return response.data;
            } catch (error) {
                consecutiveErrors.current += 1;
                throw error;
            }
        },
        enabled: !!gatewayId && !!roomId && hasTenantScope(scope),
        retry: false,
        // * Best effort (e.g. no ripening program service in this environment): after an error
        // * wait longer and longer (60 s → 120 s → 300 s) but never stop checking for good.
        refetchInterval: () => roomRunPollInterval(consecutiveErrors.current, startPending),
        refetchOnWindowFocus: false,
    });
}
